import { _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import type { AgentReportV1 } from '@/model/Agent.model';
import type { FC } from 'react';
import styles from './ScorecardCoverage.module.scss';

interface ScorecardCoverageProps {
  coverage: NonNullable<AgentReportV1['coverage']>;
}

/**
 * What the report was built from: the exact record totals, and whether any section's row-level detail
 * was capped. An absent total is omitted rather than shown as zero — the two mean different things.
 */
const ScorecardCoverage: FC<ScorecardCoverageProps> = ({ coverage }) => {
  const totals: Array<{ label: string; value: number | null | undefined }> = [
    { label: 'Animals', value: coverage.animals },
    { label: 'Alerts', value: coverage.alerts },
    { label: 'Measurements', value: coverage.measurements },
    { label: 'Dosages', value: coverage.dosages },
    { label: 'Observations', value: coverage.observations },
    { label: 'Samples', value: coverage.samples },
  ];
  const present = totals.filter((total) => _notNil(total.value));
  const hasNote = _isNotBlank(coverage.note);

  if (!_isNotEmpty(present) && !hasNote) {
    return null;
  }

  return (
    <footer className={styles.coverage} data-test-component="ScorecardCoverage">
      {_isNotEmpty(present) ? (
        <table className={styles.totals} data-test-element="totals">
          <thead>
            <tr>
              {present.map((total) => (
                <th key={total.label} scope="col" className={styles.label}>
                  {total.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {present.map((total) => (
                <td key={total.label} className={styles.value} data-test-element={total.label.toLowerCase()}>
                  {total.value}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      ) : null}
      {hasNote ? (
        <p className={styles.note} data-test-element="coverage-note">
          {coverage.note}
        </p>
      ) : null}
    </footer>
  );
};

export default ScorecardCoverage;
